import { useState, useCallback, useRef, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useOCRStore } from "@/stores/ocrStore";
import { captureError } from "@/lib/sentry";
import { calculateFileHash } from "@/lib/security/fileValidator";
import { z } from "zod";
import type {
  OCRExtractedDocument,
  OCRExtractionResponse,
  OCRMatchingResult,
  ChantierMatch,
  DoublonDetecte,
  OCRImportState,
} from "@/types/ocr";

// === SCHEMAS DE VALIDATION OCR ===
// Validation des donnees avant insertion en base (securite)
const factureInsertSchema = z.object({
  fournisseur: z.string().max(200).default("Fournisseur inconnu"),
  montant_ht: z.number().nonnegative().max(100000000).nullable(),
  montant_ttc: z.number().nonnegative().max(100000000).nullable(),
  tva_pct: z.number().min(0).max(100).default(10),
  numero_facture: z.string().max(100).nullable().optional(),
  date_facture: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).nullable().optional(),
  siret: z.string().regex(/^\d{14}$/).nullable().optional().or(z.literal("")),
});

const devisInsertSchema = z.object({
  titre: z.string().max(200).default("Devis"),
  montant_ht: z.number().nonnegative().max(100000000).nullable(),
  montant_ttc: z.number().nonnegative().max(100000000).nullable(),
  tva: z.number().min(0).max(100).nullable(),
  statut: z.enum(["brouillon", "envoye", "accepte", "refuse"]).default("brouillon"),
});

const fraisInsertSchema = z.object({
  type_frais: z.string().min(1).max(100),
  montant_ht: z.number().nonnegative().max(100000000),
  description: z.string().max(500).optional().nullable(),
  date_frais: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
});

// Type pour un chantier simplifié
export interface ChantierSimple {
  id: string;
  nom: string;
  client: string | null;
}

/**
 * Hook pour l'extraction OCR avec Claude Vision et matching multi-chantier
 */
export const useClaudeVisionOCR = (
  chantierId?: string,
  documentType?: 'facture' | 'devis' | 'ao' | 'frais',
  typeFrais?: string
) => {
  const { toast } = useToast();
  const ocrStore = useOCRStore();

  const [state, setState] = useState<OCRImportState>({
    step: 'upload',
    file: null,
    extractedData: null,
    confidence: 0,
    matchingResult: null,
    selectedChantiers: {},
    editedData: {},
    errors: [],
  });

  const [isLoading, setIsLoading] = useState(false);

  // Set des indices de commandes déjà liées (pour garder le modal ouvert)
  const [linkedCommandes, setLinkedCommandes] = useState<Set<number>>(new Set());

  // Ref pour vérifier si le composant est toujours monté (évite interruption lors du changement d'onglet)
  const isMountedRef = useRef(true);

  // Flag pour savoir si on a déjà restauré depuis le store
  const hasRestoredRef = useRef(false);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // Restaurer l'état depuis le store au montage (si même contexte)
  useEffect(() => {
    if (hasRestoredRef.current) return;

    // Vérifier si des données OCR sont en attente pour ce contexte
    const storeHasData = ocrStore.hasData();
    const sameContext = ocrStore.chantierId === chantierId &&
                        ocrStore.documentType === documentType;

    if (storeHasData && sameContext && ocrStore.extractedData) {
      if (import.meta.env.DEV) console.log('[OCR] Restauration des données depuis le store');
      setState(prev => ({
        ...prev,
        step: ocrStore.step,
        extractedData: ocrStore.extractedData,
        confidence: ocrStore.confidence,
        editedData: ocrStore.editedData,
        selectedChantiers: ocrStore.selectedChantiers,
      }));
      hasRestoredRef.current = true;
    }
  }, [chantierId, documentType, ocrStore]);

  // Réinitialiser l'état
  const reset = useCallback(() => {
    setState({
      step: 'upload',
      file: null,
      extractedData: null,
      confidence: 0,
      matchingResult: null,
      selectedChantiers: {},
      editedData: {},
      errors: [],
    });
    setLinkedCommandes(new Set());
    // Effacer aussi le store persisté
    ocrStore.clearOCRData();
    hasRestoredRef.current = false;
  }, [ocrStore]);

  // Marquer des commandes comme liées (sans fermer le modal)
  const markCommandesAsLinked = useCallback((indices: number[]) => {
    setLinkedCommandes(prev => {
      const newSet = new Set(prev);
      indices.forEach(i => newSet.add(i));
      return newSet;
    });
    // Rester sur l'étape review (ne pas reset)
    setState(prev => ({ ...prev, step: 'review' }));
  }, []);

  // Récupérer l'utilisateur et son entreprise
  const getUserAndEntreprise = async (): Promise<{entrepriseId: string | null, userId: string | null}> => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return { entrepriseId: null, userId: null };

    const { data: entreprise } = await supabase
      .from('entreprises')
      .select('id')
      .eq('proprietaire_user_id', user.id)
      .maybeSingle();

    return { entrepriseId: entreprise?.id || null, userId: user.id };
  };

  // Upload et extraction du document
  const extractDocument = useCallback(async (
    file: File,
    documentType: 'facture' | 'devis' | 'ao' | 'frais'
  ): Promise<OCRExtractionResponse | null> => {
    setIsLoading(true);
    setState(prev => ({ ...prev, step: 'extraction', file, errors: [] }));

    try {
      const { entrepriseId } = await getUserAndEntreprise();
      if (!entrepriseId) {
        toast({
          title: "Non authentifié",
          description: "Connectez-vous pour importer des documents.",
          variant: "destructive"
        });
        return null;
      }

      // Vérifier la taille (20 Mo max)
      if (file.size > 20 * 1024 * 1024) {
        const sizeMB = Math.round(file.size / (1024 * 1024));
        toast({
          title: "Fichier trop volumineux",
          description: `Ce fichier fait ${sizeMB} Mo. La limite est de 20 Mo. Compressez-le ou utilisez une qualité plus basse.`,
          variant: "destructive"
        });
        return null;
      }

      // ═══════════════════════════════════════════════════════════════════════
      // CACHE OCR - Vérifier si ce fichier a déjà été analysé
      // Économie d'appel API si le fichier est identique (même hash SHA-256)
      // ═══════════════════════════════════════════════════════════════════════
      const fileHash = await calculateFileHash(file);
      if (import.meta.env.DEV) console.log('[OCR] Hash fichier calculé:', fileHash.substring(0, 16) + '...');

      // Vérifier le cache
      const { data: cached, error: cacheError } = await supabase
        .from('ocr_cache')
        .select('ocr_result, confidence')
        .eq('entreprise_id', entrepriseId)
        .eq('file_hash', fileHash)
        .maybeSingle();

      if (cached && !cacheError) {
        // Cache hit - économie d'appel API !
        if (import.meta.env.DEV) console.log('[OCR] Cache hit - économie API');

        // Mettre à jour last_used_at
        await supabase
          .from('ocr_cache')
          .update({ last_used_at: new Date().toISOString() })
          .eq('entreprise_id', entrepriseId)
          .eq('file_hash', fileHash);

        const cachedResponse = cached.ocr_result as OCRExtractionResponse;

        // Mettre à jour l'état avec les données du cache
        if (cachedResponse.success && cachedResponse.data) {
          setState(prev => ({
            ...prev,
            step: 'review',
            extractedData: cachedResponse.data,
            confidence: cachedResponse.confidence || cached.confidence || 0
          }));

          // Persister dans le store pour survivre aux changements de page
          ocrStore.setContext(chantierId || null, documentType || null, typeFrais);
          ocrStore.setExtractedData(cachedResponse.data, cachedResponse.confidence || cached.confidence || 0);

          toast({
            title: "Document reconnu",
            description: `Ce document a déjà été analysé. Confiance: ${Math.round((cachedResponse.confidence || cached.confidence || 0) * 100)}%`
          });
        }

        setIsLoading(false);
        return cachedResponse;
      }

      // Cache miss - continuer avec l'appel API normal
      if (import.meta.env.DEV) console.log('[OCR] Cache miss - appel API');

      // Upload temporaire
      const bucket = documentType === 'ao' ? 'documents' :
                     documentType === 'facture' ? 'factures' :
                     documentType === 'frais' ? 'factures' : 'devis';
      // Sanitizer le nom de fichier (supprimer caractères spéciaux comme [1], espaces, etc.)
      const sanitizedFileName = file.name.replace(/[^a-zA-Z0-9._-]/g, '_');
      const tempPath = `${entrepriseId}/temp-${Date.now()}-${sanitizedFileName}`;

      const { error: uploadError } = await supabase.storage
        .from(bucket)
        .upload(tempPath, file);

      if (uploadError) throw uploadError;

      // URL signée
      const { data: urlData } = await supabase.storage
        .from(bucket)
        .createSignedUrl(tempPath, 3600);

      if (!urlData?.signedUrl) throw new Error("Impossible de générer l'URL");

      toast({
        title: "Extraction en cours...",
        description: "Analyse IA du document.",
        duration: 3000
      });

      // Appel Edge Function
      const { data, error } = await supabase.functions.invoke('ocr-claude-vision', {
        body: {
          fileUrl: urlData.signedUrl,
          documentType: documentType === 'ao' ? 'ao' : documentType === 'frais' ? 'frais_annexe' : 'facture',
          entrepriseId
        }
      });

      // Nettoyer le fichier temporaire
      await supabase.storage.from(bucket).remove([tempPath]);

      if (error) {
        console.error('[OCR] Erreur:', error);
        // Message plus explicite selon l'erreur
        let errorMsg = "L'analyse du document a échoué. ";
        if (error.message?.includes("timeout")) {
          errorMsg += "Le serveur a mis trop de temps à répondre.";
        } else if (error.message?.includes("format") || error.message?.includes("type")) {
          errorMsg += "Format non supporté. Utilisez PDF, JPG ou PNG.";
        } else if (error.message?.includes("corrupt") || error.message?.includes("read")) {
          errorMsg += "Le document semble endommagé ou illisible.";
        } else {
          errorMsg += "Vérifiez que le document est lisible et réessayez.";
        }
        toast({
          title: "Impossible d'analyser le document",
          description: errorMsg,
          variant: "destructive"
        });
        return null;
      }

      const response = data as OCRExtractionResponse;

      // Vérifier que le composant est toujours monté avant de mettre à jour l'état
      if (!isMountedRef.current) {
        if (import.meta.env.DEV) console.log('[OCR] Composant démonté, abandon de la mise à jour');
        return response;
      }

      if (response.success) {
        setState(prev => ({
          ...prev,
          step: 'review',
          extractedData: response.data,
          confidence: response.confidence
        }));

        // Persister dans le store pour survivre aux changements de page
        ocrStore.setContext(chantierId || null, documentType || null, typeFrais);
        ocrStore.setExtractedData(response.data, response.confidence);

        // ═══════════════════════════════════════════════════════════════════
        // CACHE OCR - Stocker le résultat pour éviter les appels doublons
        // ═══════════════════════════════════════════════════════════════════
        try {
          await supabase
            .from('ocr_cache')
            .upsert({
              entreprise_id: entrepriseId,
              file_hash: fileHash,
              file_name: file.name,
              file_size: file.size,
              document_type: documentType,
              ocr_result: response,
              confidence: response.confidence,
            }, { onConflict: 'entreprise_id,file_hash' });
          if (import.meta.env.DEV) console.log('[OCR] Résultat stocké dans le cache');
        } catch (cacheInsertError) {
          if (import.meta.env.DEV) console.warn('[OCR] Erreur stockage cache (non bloquant):', cacheInsertError);
        }

        toast({
          title: "Extraction réussie",
          description: `Confiance: ${Math.round(response.confidence * 100)}%`
        });
      } else if ('partialData' in response) {
        setState(prev => ({
          ...prev,
          step: 'review',
          extractedData: response.partialData,
          confidence: response.confidence
        }));

        // Persister dans le store même pour les données partielles
        ocrStore.setContext(chantierId || null, documentType || null, typeFrais);
        ocrStore.setExtractedData(response.partialData, response.confidence);

        // ═══════════════════════════════════════════════════════════════════
        // CACHE OCR - Stocker aussi les résultats partiels (évite re-appel API)
        // ═══════════════════════════════════════════════════════════════════
        try {
          await supabase
            .from('ocr_cache')
            .upsert({
              entreprise_id: entrepriseId,
              file_hash: fileHash,
              file_name: file.name,
              file_size: file.size,
              document_type: documentType,
              ocr_result: response,
              confidence: response.confidence,
            }, { onConflict: 'entreprise_id,file_hash' });
          if (import.meta.env.DEV) console.log('[OCR] Résultat partiel stocké dans le cache');
        } catch (cacheInsertError) {
          if (import.meta.env.DEV) console.warn('[OCR] Erreur stockage cache partiel (non bloquant):', cacheInsertError);
        }

        toast({
          title: "Extraction partielle",
          description: response.message,
          variant: "default"
        });
      }

      return response;

    } catch (error: any) {
      console.error('[OCR] Exception:', error);
      captureError(error, {
        context: 'ocr_extraction',
        documentType,
        fileSize: file.size,
        fileName: file.name
      });
      toast({
        title: "Erreur inattendue",
        description: "Une erreur technique s'est produite. Réessayez ou contactez le support si le problème persiste.",
        variant: "destructive"
      });
      setState(prev => ({ ...prev, errors: [error.message] }));
      return null;
    } finally {
      setIsLoading(false);
    }
  }, [toast]);

  // Matcher les références avec les chantiers existants
  const matchChantiers = useCallback(async (): Promise<OCRMatchingResult | null> => {
    if (!state.extractedData) return null;

    setIsLoading(true);
    setState(prev => ({ ...prev, step: 'matching' }));

    try {
      const { entrepriseId } = await getUserAndEntreprise();
      if (!entrepriseId) return null;

      // Récupérer tous les chantiers de l'entreprise
      const { data: chantiers, error } = await supabase
        .from('chantiers')
        .select('id, nom, client_nom')
        .eq('entreprise_id', entrepriseId);

      if (error) throw error;

      const references = state.extractedData.references_chantier || [];
      const matches: ChantierMatch[] = [];

      // Pour chaque référence, chercher le meilleur match
      for (const ref of references) {
        const refLower = ref.reference.toLowerCase();

        for (const chantier of chantiers || []) {
          const nomLower = chantier.nom.toLowerCase();
          const clientLower = (chantier.client_nom || '').toLowerCase();

          // Score de matching simple (à améliorer avec Levenshtein si besoin)
          let score = 0;
          if (nomLower.includes(refLower) || refLower.includes(nomLower)) {
            score = 0.8;
          } else if (clientLower.includes(refLower)) {
            score = 0.6;
          }

          if (score > 0.5) {
            // Vérifier si ce document existe déjà pour ce chantier
            const hash = (state.extractedData as any)?.hash_anti_doublon;
            let dejaLie = false;

            if (hash) {
              const { data: existing } = await supabase
                .from('factures_fournisseurs')
                .select('id')
                .eq('chantier_id', chantier.id)
                .eq('hash_anti_doublon', hash)
                .maybeSingle();

              dejaLie = !!existing;
            }

            matches.push({
              chantier_id: chantier.id,
              chantier_nom: chantier.nom,
              reference_detectee: ref.reference,
              score_matching: score,
              deja_lie: dejaLie
            });
          }
        }
      }

      // Détecter les doublons globaux
      const doublons: DoublonDetecte[] = [];
      const numero = state.extractedData.numero_document;
      const date = state.extractedData.date_document;
      const ttc = state.extractedData.totaux?.total_ttc;

      if (numero && date) {
        const { data: existingDocs } = await supabase
          .from('factures_fournisseurs')
          .select(`
            id,
            numero_facture,
            date_facture,
            montant_ttc,
            chantiers(nom)
          `)
          .eq('numero_facture', numero);

        if (existingDocs && existingDocs.length > 0) {
          for (const doc of existingDocs) {
            doublons.push({
              document_id: doc.id,
              numero_document: doc.numero_facture || '',
              date_document: doc.date_facture || '',
              montant_ttc: doc.montant_ttc || 0,
              chantier_nom: (doc.chantiers as any)?.nom || 'Non lié',
              hash: ''
            });
          }
        }
      }

      const result: OCRMatchingResult = {
        references_chantier: references,
        matches,
        doublons_detectes: doublons
      };

      setState(prev => ({ ...prev, matchingResult: result }));

      if (doublons.length > 0) {
        toast({
          title: "Doublons détectés",
          description: `${doublons.length} document(s) similaire(s) trouvé(s).`,
          variant: "default"
        });
      }

      return result;

    } catch (error: any) {
      console.error('[OCR] Erreur matching:', error);
      captureError(error, {
        context: 'ocr_matching_chantiers',
        extractedDataPresent: !!state.extractedData,
        referencesCount: state.extractedData?.references_chantier?.length || 0
      });
      toast({
        title: "Erreur",
        description: error.message,
        variant: "destructive"
      });
      return null;
    } finally {
      setIsLoading(false);
    }
  }, [state.extractedData, toast]);

  // Sélectionner un chantier pour une référence
  const selectChantier = useCallback((reference: string, chantierIdSelected: string) => {
    setState(prev => ({
      ...prev,
      selectedChantiers: {
        ...prev.selectedChantiers,
        [reference]: chantierIdSelected
      }
    }));
    // Synchroniser avec le store
    ocrStore.selectChantier(reference, chantierIdSelected);
  }, [ocrStore]);

  // Modifier une donnée extraite
  const editData = useCallback((field: string, value: any) => {
    setState(prev => ({
      ...prev,
      editedData: {
        ...prev.editedData,
        [field]: value
      }
    }));
    // Synchroniser avec le store
    ocrStore.updateEditedField(field, value);
  }, [ocrStore]);

  // Supprimer un doublon existant
  const deleteDuplicateDocument = useCallback(async (documentId: string): Promise<boolean> => {
    try {
      const { error } = await supabase
        .from('factures_fournisseurs')
        .delete()
        .eq('id', documentId);

      if (error) throw error;

      toast({
        title: "Document supprimé",
        description: "Le doublon a été supprimé."
      });

      // Mettre à jour la liste des doublons
      setState(prev => ({
        ...prev,
        matchingResult: prev.matchingResult ? {
          ...prev.matchingResult,
          doublons_detectes: prev.matchingResult.doublons_detectes.filter(
            d => d.document_id !== documentId
          )
        } : null
      }));

      return true;

    } catch (error: any) {
      console.error('[OCR] Erreur suppression:', error);
      captureError(error, {
        context: 'ocr_delete_duplicate',
        documentId
      });
      toast({
        title: "Erreur",
        description: error.message,
        variant: "destructive"
      });
      return false;
    }
  }, [toast]);

  // Récupérer tous les chantiers de l'entreprise
  const getAllChantiers = useCallback(async (): Promise<ChantierSimple[]> => {
    try {
      const { entrepriseId } = await getUserAndEntreprise();
      if (!entrepriseId) return [];

      const { data, error } = await supabase
        .from('chantiers')
        .select('id, nom_chantier, client')
        .eq('entreprise_id', entrepriseId)
        .order('nom_chantier', { ascending: true });

      if (error) throw error;
      // Mapper nom_chantier -> nom pour correspondre à l'interface ChantierSimple
      return (data || []).map(c => ({
        id: c.id,
        nom: c.nom_chantier,
        client: c.client
      }));

    } catch (error: any) {
      console.error('[OCR] Erreur chargement chantiers:', error);
      captureError(error, {
        context: 'ocr_get_all_chantiers'
      });
      return [];
    }
  }, []);

  // Récupérer la référence d'un chantier par son ID
  const getChantierReference = useCallback(async (chantierId: string): Promise<string | null> => {
    try {
      const { data, error } = await supabase
        .from('chantiers')
        .select('reference_chantier')
        .eq('id', chantierId)
        .single();

      if (error || !data) return null;
      return data.reference_chantier || null;
    } catch (error) {
      console.error('[OCR] Erreur récupération référence chantier:', error);
      return null;
    }
  }, []);

  // Créer un nouveau chantier et retourner son ID
  const createChantier = useCallback(async (
    nom: string,
    clientNom?: string,
    reference?: string,
    statut?: string
  ): Promise<string | null> => {
    try {
      const { entrepriseId } = await getUserAndEntreprise();
      if (!entrepriseId) {
        toast({
          title: "Erreur",
          description: "Entreprise non trouvée",
          variant: "destructive"
        });
        return null;
      }

      const { data, error } = await supabase
        .from('chantiers')
        .insert({
          entreprise_id: entrepriseId,
          nom_chantier: nom,
          client: clientNom || '',
          reference_chantier: reference || null,
          etat_chantier: statut || 'en_cours'
        })
        .select('id')
        .single();

      if (error) throw error;

      toast({
        title: "Chantier créé",
        description: `Le chantier "${nom}" a été créé.`
      });

      return data?.id || null;

    } catch (error: any) {
      console.error('[OCR] Erreur création chantier:', error);
      captureError(error, {
        context: 'ocr_create_chantier',
        chantierNom: nom,
        clientNom
      });
      toast({
        title: "Erreur",
        description: error.message,
        variant: "destructive"
      });
      return null;
    }
  }, [toast]);

  // Sauvegarder le document
  // chantierId optionnel : passé depuis le composant parent ou détecté via matching
  // selectedCommandesIndices : indices des commandes à inclure (si multi-commande)
  // keepOpen : ne pas reset après sauvegarde (pour multi-commandes)
  // typeFrais : type de frais pour les frais annexes
  const saveDocument = useCallback(async (
    documentType: 'facture' | 'devis' | 'frais',
    parentChantierId?: string,
    selectedCommandesIndices?: number[],
    keepOpen?: boolean,
    devisStatut?: string,
    typeFrais?: string
  ): Promise<string | null> => {
    if (!state.extractedData) return null;

    setIsLoading(true);
    setState(prev => ({ ...prev, step: 'saving' }));

    try {
      const { entrepriseId, userId } = await getUserAndEntreprise();
      if (!entrepriseId || !userId) throw new Error("Entreprise non trouvée");

      // Fusionner les données extraites et éditées
      const data = { ...state.extractedData, ...state.editedData };

      // Déterminer le chantier : priorité au parent, sinon matching, sinon null
      const selectedRefs = Object.keys(state.selectedChantiers);
      const chantierId = parentChantierId
        || (selectedRefs.length > 0 ? state.selectedChantiers[selectedRefs[0]] : null);

      // Si des commandes spécifiques sont sélectionnées, filtrer les lignes et recalculer les totaux
      let finalData = data;
      if (selectedCommandesIndices && selectedCommandesIndices.length > 0 && data.commandes && data.commandes.length > 1) {
        // Récupérer les indices de lignes à inclure
        const selectedLignesIndices = new Set<number>();
        let totalHT = 0;
        let totalTTC = 0;

        for (const cmdIdx of selectedCommandesIndices) {
          const cmd = data.commandes[cmdIdx];
          if (cmd) {
            cmd.lignes_indices.forEach((idx: number) => selectedLignesIndices.add(idx));
            totalHT += cmd.total_ht || 0;
            totalTTC += cmd.total_ttc || 0;
          }
        }

        // Si totalTTC est 0 mais totalHT > 0, calculer automatiquement TTC = HT + TVA
        const tauxTVA = data.totaux?.taux_tva || 10;
        if (totalTTC === 0 && totalHT > 0) {
          totalTTC = totalHT * (1 + tauxTVA / 100);
        }

        // Filtrer les lignes
        const filteredLignes = data.lignes.filter((_, idx) => selectedLignesIndices.has(idx));

        // Calculer TVA correctement (HT * taux)
        const montantTVA = totalHT * tauxTVA / 100;

        finalData = {
          ...data,
          lignes: filteredLignes,
          totaux: {
            ...data.totaux,
            total_ht: totalHT,
            total_ttc: totalTTC,
            montant_tva: montantTVA,
          },
          // Garder seulement les commandes sélectionnées
          commandes: selectedCommandesIndices.map(idx => data.commandes![idx]).filter(Boolean),
        };
      }

      if (documentType === 'facture') {
        // Toujours recalculer TTC = HT × (1 + TVA%) pour garantir la cohérence
        const ht = finalData.totaux?.total_ht || 0;
        const tvaPct = finalData.totaux?.taux_tva || 10;
        const ttc = ht > 0
          ? ht * (1 + tvaPct / 100)
          : (finalData.totaux?.total_ttc || null);

        // === VALIDATION SECURITE des donnees OCR avant insertion ===
        const factureValidation = factureInsertSchema.safeParse({
          fournisseur: finalData.emetteur?.nom || 'Fournisseur inconnu',
          montant_ht: ht || null,
          montant_ttc: ttc,
          tva_pct: tvaPct,
          numero_facture: finalData.numero_document,
          date_facture: finalData.date_document,
          siret: finalData.emetteur?.siret || null,
        });

        if (!factureValidation.success) {
          console.error('[OCR] Validation facture echouee:', factureValidation.error.errors);
          throw new Error(`Données invalides: ${factureValidation.error.errors.map(e => e.message).join(', ')}`);
        }

        const { data: inserted, error } = await supabase
          .from('factures_fournisseurs')
          .insert({
            entreprise_id: entrepriseId,
            created_by: userId,
            chantier_id: chantierId,
            fournisseur: factureValidation.data.fournisseur,
            categorie: 'Autres',
            numero_facture: factureValidation.data.numero_facture,
            date_facture: factureValidation.data.date_facture,
            montant_ht: factureValidation.data.montant_ht,
            montant_ttc: factureValidation.data.montant_ttc,
            tva_pct: factureValidation.data.tva_pct,
            siret: factureValidation.data.siret,
            extraction_json: finalData as any,
            hash_anti_doublon: (state as any).hash_anti_doublon || null
          })
          .select('id')
          .single();

        if (error) throw error;

        toast({
          title: "Facture enregistrée",
          description: `N° ${finalData.numero_document || 'sans numéro'}`
        });

        // Ne pas reset si keepOpen (mode multi-commandes)
        if (!keepOpen) {
          reset();
        } else {
          // Revenir à l'étape review pour continuer
          setState(prev => ({ ...prev, step: 'review' }));
        }
        return inserted?.id || null;

      } else if (documentType === 'frais') {
        // Sauvegarde des frais annexes - utiliser montant_ht pour les calculs de rentabilité
        const ht = finalData.totaux?.total_ht || 0;

        // === VALIDATION SECURITE des donnees OCR avant insertion ===
        const fraisValidation = fraisInsertSchema.safeParse({
          type_frais: typeFrais || 'Autre',
          montant_ht: ht,
          date_frais: finalData.date_document || new Date().toISOString().split('T')[0],
          description: finalData.objet_travaux || finalData.emetteur?.nom || 'Importé par Scan IA',
        });

        if (!fraisValidation.success) {
          console.error('[OCR] Validation frais echouee:', fraisValidation.error.errors);
          throw new Error(`Données invalides: ${fraisValidation.error.errors.map(e => e.message).join(', ')}`);
        }

        const { data: inserted, error } = await supabase
          .from('frais_chantier')
          .insert({
            entreprise_id: entrepriseId,
            chantier_id: chantierId,
            type_frais: fraisValidation.data.type_frais,
            montant_ht: fraisValidation.data.montant_ht,
            montant_total: fraisValidation.data.montant_ht, // Rétrocompatibilité
            date_frais: fraisValidation.data.date_frais,
            description: fraisValidation.data.description,
          })
          .select('id')
          .single();

        if (error) throw error;

        toast({
          title: "Coût annexe enregistré",
          description: `Montant HT: ${ht.toLocaleString('fr-FR')} €`
        });

        reset();
        return inserted?.id || null;

      } else {
        // Devis
        // === VALIDATION SECURITE des donnees OCR avant insertion ===
        const devisValidation = devisInsertSchema.safeParse({
          titre: finalData.objet_travaux || 'Devis',
          montant_ht: finalData.totaux?.total_ht ?? null,
          montant_ttc: finalData.totaux?.total_ttc ?? null,
          tva: finalData.totaux?.taux_tva ?? null,
          statut: devisStatut || 'brouillon',
        });

        if (!devisValidation.success) {
          console.error('[OCR] Validation devis echouee:', devisValidation.error.errors);
          throw new Error(`Données invalides: ${devisValidation.error.errors.map(e => e.message).join(', ')}`);
        }

        const { data: inserted, error } = await supabase
          .from('devis')
          .insert({
            entreprise_id: entrepriseId,
            chantier_id: chantierId,
            titre: devisValidation.data.titre,
            montant_ht: devisValidation.data.montant_ht,
            montant_ttc: devisValidation.data.montant_ttc,
            tva: devisValidation.data.tva,
            extraction_json: finalData as any,
            statut: devisValidation.data.statut
          })
          .select('id')
          .single();

        if (error) throw error;

        toast({
          title: "Devis enregistré",
          description: `N° ${finalData.numero_document || 'sans numéro'}`
        });

        // Ne pas reset si keepOpen (mode multi-commandes)
        if (!keepOpen) {
          reset();
        } else {
          // Revenir à l'étape review pour continuer
          setState(prev => ({ ...prev, step: 'review' }));
        }
        return inserted?.id || null;
      }

    } catch (error: any) {
      console.error('[OCR] Erreur sauvegarde:', error);
      captureError(error, {
        context: 'ocr_save_document',
        documentType,
        chantierId: parentChantierId,
        hasExtractedData: !!state.extractedData
      });
      toast({
        title: "Erreur",
        description: error.message,
        variant: "destructive"
      });
      setState(prev => ({ ...prev, errors: [error.message] }));
      return null;
    } finally {
      setIsLoading(false);
    }
  }, [state, reset, toast]);

  // Vérifier si des données OCR sont en attente dans le store
  const hasStoredData = ocrStore.hasData() &&
                        ocrStore.chantierId === chantierId &&
                        ocrStore.documentType === documentType;

  return {
    state,
    isLoading,
    linkedCommandes,
    hasStoredData,
    extractDocument,
    matchChantiers,
    selectChantier,
    editData,
    deleteDuplicateDocument,
    getAllChantiers,
    getChantierReference,
    createChantier,
    saveDocument,
    markCommandesAsLinked,
    reset
  };
};
